import { fontFamilies, r, s, typographyTokens } from '@/theme/tokens';
import {
  settingsCopyTextSx,
  settingsWideFieldMeasureSx,
} from '@/ui/composites/settingsPageTypographySx';
import type { SxProps, Theme } from '@/ui/primitives/types';

type ThemeSxObject = Exclude<SxProps<Theme>, readonly unknown[] | ((theme: Theme) => unknown)>;

export const projectSettingsPopoverPaperSx: SxProps<Theme> = {
  mt: s('xs'),
  width: 'max-content',
  minWidth: 0,
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: `${r('sm')} !important`,
  backgroundColor: 'var(--surface-card)',
  backgroundImage: 'none',
  overflow: 'hidden',
};

export const projectSettingsAccordionSurfaceSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: r('lg'),
  overflow: 'hidden',
  padding: s('xl'),
  '&::before': { display: 'none' },
};

export const projectSettingsAccordionExpandIconSx: SxProps<Theme> = {
  fontSize: 28,
};

export const projectSettingsAccordionExpandIconWrapperSx: SxProps<Theme> = {
  display: 'inline-flex',
};

export const projectSettingsAccordionSummarySx = (disabled: boolean): ThemeSxObject => ({
  padding: s('none'),
  minHeight: 'auto',
  alignItems: 'flex-start',
  cursor: 'default !important',
  userSelect: 'text',
  WebkitUserSelect: 'text',
  '&.MuiButtonBase-root': {
    cursor: 'default !important',
    userSelect: 'text',
    WebkitUserSelect: 'text',
  },
  '& .MuiAccordionSummary-content': {
    margin: s('none'),
    alignItems: 'flex-start',
    cursor: 'default !important',
    userSelect: 'text',
    WebkitUserSelect: 'text',
  },
  '& .MuiAccordionSummary-content .MuiTypography-root': {
    userSelect: 'text',
    WebkitUserSelect: 'text',
  },
  '& .MuiAccordionSummary-expandIconWrapper': {
    alignSelf: 'center',
    cursor: disabled ? 'not-allowed' : 'pointer',
    pointerEvents: 'auto',
    opacity: disabled ? 0.45 : 1,
  },
});

export const projectSettingsAccordionSummaryTitleStackSx: SxProps<Theme> = {
  width: '100%',
};

export const projectSettingsAccordionDetailsSx: SxProps<Theme> = {
  padding: s('none'),
  paddingTop: s('lg'),
};

export const projectSettingsValidationPreviewSurfaceSx: SxProps<Theme> = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: r('lg'),
  backgroundColor: 'var(--surface-page)',
  px: s('md'),
  py: s('md'),
};

export const projectSettingsUploadSnippetBlockSx: SxProps<Theme> = {
  width: '100%',
  backgroundColor: 'var(--surface-inset)',
};

export const projectSettingsProjectIdTextSx: SxProps<Theme> = {
  display: 'block',
  fontFamily: fontFamilies.mono,
};

export const projectSettingsProjectIdCopyButtonSx: SxProps<Theme> = {
  position: 'absolute',
  top: '50%',
  right: `-${s('md')}`,
  transform: 'translateY(-50%)',
  borderRadius: r('pill'),
  '&:hover': {
    backgroundColor: 'action.hover',
  },
};

export const projectSettingsMetadataRowSx: SxProps<Theme> = [
  {
    display: 'flex',
    flexWrap: { xs: 'wrap', sm: 'nowrap' },
    gap: { xs: s('sm'), sm: s('md') },
    width: '100%',
    alignItems: 'flex-end',
  },
  settingsWideFieldMeasureSx,
];

export const projectSettingsHelperTextSx: SxProps<Theme> = [
  settingsCopyTextSx,
  {
    maxWidth: '62ch',
    m: 0,
  },
];

export const projectSettingsMetadataLabelTextSx: SxProps<Theme> = [
  settingsCopyTextSx,
  {
    maxWidth: 'none',
    m: 0,
  },
];

export const projectSettingsMetadataCellSx: SxProps<Theme> = {
  flex: 1,
  minWidth: 0,
};

export const projectSettingsMetadataActionSpacerSx: SxProps<Theme> = {
  display: { xs: 'none', sm: 'inline-flex' },
  visibility: 'hidden',
  pointerEvents: 'none',
};

export const projectSettingsApiKeyBannerSx: SxProps<Theme> = {
  mt: s('sm'),
  mb: s('sm'),
  '& .MuiAlert-message': {
    py: s('xs'),
  },
};

export const projectSettingsDetailsFieldMeasure = '320px';

export const projectSettingsDetailsNameBadgeRowSx: SxProps<Theme> = {
  display: 'grid',
  gridTemplateColumns: {
    xs: 'minmax(0, 1fr) auto',
    sm: `${projectSettingsDetailsFieldMeasure} auto`,
  },
  alignItems: 'flex-end',
  columnGap: s('lg'),
  width: '100%',
  maxWidth: '100%',
};

export const projectSettingsDetailsFieldSx: SxProps<Theme> = {
  width: { xs: '100%', sm: projectSettingsDetailsFieldMeasure },
  maxWidth: '100%',
};

export const projectSettingsDetailsNameFieldSx: SxProps<Theme> = {
  width: { xs: '100%', sm: projectSettingsDetailsFieldMeasure },
  minWidth: 0,
};

export const projectSettingsDetailsBadgePreviewSx: SxProps<Theme> = {
  mb: '4px',
  flex: '0 0 auto',
};

export const projectSettingsDetailsBadgeFieldSx: SxProps<Theme> = {
  mt: s('sm'),
};

export const projectWebhookFieldRowSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'flex-end',
  gap: s('md'),
  width: '100%',
};

export const projectSettingsRevealTokenFieldRowSx: SxProps<Theme> = {
  ...projectWebhookFieldRowSx,
};

export const projectSettingsRevealTokenCopyButtonSx: SxProps<Theme> = {
  flexShrink: 0,
};

export const projectWebhookFieldCellSx: SxProps<Theme> = {
  flex: 1,
  minWidth: 0,
};

export const projectWebhookFieldInputTextSx: SxProps<Theme> = {
  '& .MuiOutlinedInput-input': {
    color: 'text.secondary',
  },
};

export const projectWebhookUrlFieldInputTextSx: SxProps<Theme> = {
  '& .MuiOutlinedInput-input': {
    color: 'text.secondary',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
};

export const projectWebhookFixedActionButtonSx = {
  minWidth: 56,
  width: 56,
  maxWidth: 56,
};

export const projectSettingsUploadTokenMetadataPanelSx: SxProps<Theme> = {
  py: s('md'),
};

export const projectSettingsTextCellSx: SxProps<Theme> = {
  minWidth: 0,
};

export const projectSettingsUploadStatusBannerSx: SxProps<Theme> = {
  px: s('lg'),
  '& .MuiAlert-icon': {
    marginRight: s('md'),
  },
  '& .MuiAlert-message': {
    flex: 1,
  },
};

export const projectSettingsUploadStatusBannerInnerSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: s('md'),
  width: '100%',
};

export const projectSettingsUploadStatusBannerLinkSx: SxProps<Theme> = {
  flexShrink: 0,
  fontWeight: typographyTokens.bodyStrong.fontWeight,
};

export const projectUploadSnippetTabsSx: SxProps<Theme> = {
  minHeight: '30px !important',
  '& .MuiTabs-flexContainer': {
    gap: s('lg'),
  },
  '& .MuiTab-root': {
    fontSize: '15px',
    minHeight: '30px !important',
    height: '30px !important',
    minWidth: 0,
    paddingLeft: s('md'),
    paddingRight: s('md'),
  },
};

export const projectUploadSnippetTabSx: SxProps<Theme> = {
  paddingBottom: s('xs'),
};

export const projectUploadSnippetTabsDividerSx: SxProps<Theme> = {
  borderBottom: 1,
  borderColor: 'divider',
};

export const projectUploadSnippetContentSx: SxProps<Theme> = {
  mt: s('xs'),
};

export const projectSettingsValidationPanelSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: r('md'),
  backgroundColor: 'var(--surface-page) !important',
  px: s('xl'),
  py: s('lg'),
};

export const projectSettingsValidationPanelActionRowSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: s('md'),
  flexShrink: 0,
};

export const projectSettingsCsvMappingPanelGroupSx = (withDivider: boolean): SxProps<Theme> => [
  {
    minWidth: 0,
    width: '100%',
  },
  ...(withDivider ? [{ borderTop: 1, borderColor: 'divider', mt: s('sm'), pt: s('lg') }] : []),
];

export const projectSettingsCsvMappingPanelDividerSx: SxProps<Theme> = {
  borderTop: 1,
  borderColor: 'divider',
  mt: s('md'),
  pt: s('lg'),
};

export const projectSettingsCsvMappingPanelTitleSx: SxProps<Theme> = {
  fontSize: (theme) => theme.typography.subtitle1.fontSize,
  lineHeight: (theme) => theme.typography.subtitle1.lineHeight,
  fontWeight: (theme) => theme.typography.subtitle1.fontWeight,
};

export const projectSettingsCsvMappingPanelHeaderSx: SxProps<Theme> = {
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  width: '100%',
  minHeight: '32px',
};

export const projectSettingsCsvMappingPanelContentSx: SxProps<Theme> = {
  mt: s('md'),
};

export const projectSettingsCsvMappingPanelActionRowSx: SxProps<Theme> = {
  display: 'grid',
  gridTemplateColumns: {
    xs: '1fr',
    sm: '80px minmax(0, 1fr) 24px',
  },
  columnGap: s('lg'),
  mt: s('sm'),
  '& > *': {
    gridColumn: {
      xs: '1 / -1',
      sm: '2 / -1',
    },
    justifySelf: 'end',
  },
};

export const projectSettingsCsvMappingFieldListSx: SxProps<Theme> = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: r('md'),
  backgroundColor: 'var(--surface-page)',
  overflow: 'hidden',
};

export const projectSettingsActionRowSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: s('sm'),
};

export const projectSettingsPanelActionRowSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: s('md'),
  mt: s('sm'),
  width: '100%',
};

export const projectSettingsPanelActionRowInnerSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: s('sm'),
};

export const projectSettingsOverviewRowSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  gap: s('sm'),
};

export const projectSettingsOverviewPairLabelSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'baseline',
  gap: s('sm'),
  flexWrap: 'wrap',
};

export const projectSettingsOverviewDividerSx: SxProps<Theme> = {
  ...settingsCopyTextSx,
  color: 'textSecondary',
} as const;

export const projectSettingsOverviewLabelTextSx: SxProps<Theme> = settingsCopyTextSx;

export const projectSettingsOverviewPanelSx = {
  width: '100%',
  py: s('md'),
};

export const projectSettingsProjectIdCopyContainerSx: SxProps<Theme> = {
  position: 'relative',
  pr: s('3xl'),
};

export const projectSettingsDetailsBadgeFieldColumnWidth = {
  xs: 'minmax(0, 1fr)',
  sm: projectSettingsDetailsFieldMeasure,
};
